import { useRef, useState } from "react";
import { apiRequest, isAbortError } from "../../lib/apiClient";
import { useCollection } from "../../lib/useCollection";
import { useToast } from "../../components/ui/ToastProvider";
import { useConfirm } from "../../components/ui/ConfirmProvider";
import { AsyncSection } from "../../components/ui/AsyncSection";
import { TextField, FileField } from "../../components/ui/Field";

const FALLBACK_IMAGE = "/assets/sholay.jpeg";
const EMPTY_FORM = { title: "", year: "", director: "", cast: "" };

export function FilmsPanel() {
  const { status, data: films, error, reload, setData } = useCollection(
    "/api/films",
    (payload) => (Array.isArray(payload) ? payload : [])
  );

  const [form, setForm] = useState(EMPTY_FORM);
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);

  const fileInputRef = useRef(null);
  const toast = useToast();
  const confirm = useConfirm();

  const updateField = (field) => (value) => setForm((current) => ({ ...current, [field]: value }));

  const handleAdd = async (event) => {
    event.preventDefault();

    if (!form.title.trim()) {
      toast.error("A title is required.");
      return;
    }

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => formData.append(key, value));
    if (image) formData.append("image", image);

    setSaving(true);
    try {
      await apiRequest("/api/films/add", { method: "POST", auth: true, body: formData });

      setForm(EMPTY_FORM);
      setImage(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success("Film added.");
      reload();
    } catch (err) {
      if (!isAbortError(err)) toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (film) => {
    const confirmed = await confirm({
      title: "Delete this film?",
      message: `“${film.title}” will be removed from the films page permanently.`,
    });
    if (!confirmed) return;

    try {
      await apiRequest(`/api/films/delete/${film._id}`, { method: "DELETE", auth: true });
      setData((current) => current.filter((item) => item._id !== film._id));
      toast.success("Film deleted.");
    } catch (err) {
      if (!isAbortError(err)) toast.error(err.message);
    }
  };

  return (
    <section className="panel" aria-labelledby="films-heading">
      <div className="upload-card">
        <h2 id="films-heading">Films</h2>

        <form onSubmit={handleAdd}>
          <div className="film-inputs">
            <TextField label="Title" value={form.title} onChange={updateField("title")} disabled={saving} required />
            <TextField label="Year" type="number" min="1900" max="2100" value={form.year} onChange={updateField("year")} disabled={saving} />
            <TextField label="Director" value={form.director} onChange={updateField("director")} disabled={saving} />
            <TextField label="Cast" value={form.cast} onChange={updateField("cast")} disabled={saving} />
          </div>

          <div className="form-row-split">
            <FileField
              label="Poster"
              hint="Optional. Maximum 15MB."
              inputRef={fileInputRef}
              onChange={setImage}
              disabled={saving}
            />

            <button type="submit" disabled={saving}>
              {saving ? "Adding…" : "Add film"}
            </button>
          </div>
        </form>
      </div>

      <AsyncSection
        status={status}
        error={error}
        isEmpty={films?.length === 0}
        emptyMessage="No films yet."
        onRetry={reload}
      >
        <ul className="slides-grid">
          {films?.map((film) => (
            <li key={film._id} className="slide-card">
              <img
                src={film.image && film.image !== "null" ? film.image : FALLBACK_IMAGE}
                alt={`Poster for ${film.title}`}
                loading="lazy"
                decoding="async"
                onError={(event) => { event.target.src = FALLBACK_IMAGE; }}
              />
              <h3 className="slide-card__title">
                {film.title}{film.year ? ` (${film.year})` : ""}
              </h3>
              <button
                type="button"
                className="delete-btn"
                onClick={() => handleDelete(film)}
                aria-label={`Delete film: ${film.title}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      </AsyncSection>
    </section>
  );
}
